// ============================================================
//  StoreSettings.js — name, logo, cover, WhatsApp number, keywords,
//  welcome message, open/closed.
// ============================================================
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { storeAction, uploadStoreImage } from '../../api/client';
import { Switch } from './storeUi';

export default function StoreSettings({ bId, store, admins = [], onStore }) {
  const [f, setF] = useState({
    name: store.name, about: store.about || '', waNumber: store.waNumber || '', keywords: (store.keywords || []).join(', '),
    welcome: store.welcome || '', isOpen: store.isOpen, closedMsg: store.closedMsg || '',
    alerts: store.alerts !== false, lowStock: String(store.lowStock ?? 3),
  });
  const [busy, setBusy] = useState('');
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  async function save() {
    setBusy('save');
    try { const res = await storeAction('settings', bId, { ...f, lowStock: Number(f.lowStock) }); onStore(res.data.store); toast.success('Saved'); }
    catch (e) { toast.error(e?.response?.data?.message || 'Could not save'); }
    setBusy('');
  }
  async function upload(which, file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5 MB'); return; }
    setBusy(which);
    try { const res = await uploadStoreImage(bId, which, file); onStore(res.data.store); toast.success('Updated'); }
    catch (e) { toast.error(e?.response?.data?.message || 'Upload failed'); }
    setBusy('');
  }
  async function removeImg(which) {
    try { const res = await storeAction('remove_image', bId, { which }); onStore(res.data.store); }
    catch (e) { toast.error('Could not remove'); }
  }

  return (
    <div className="stp-col">
      <section className="stp-card" aria-labelledby="stp-look">
        <h2 id="stp-look">Your store</h2>
        <div className="stp-images">
          <div className="stp-imgbox is-cover">
            {store.cover ? <img src={store.cover} alt="Cover" /> : <span>Cover photo<small>wide, shown at the top of your store and in WhatsApp</small></span>}
            <div className="stp-imgbtns">
              <label className="stp-btn stp-btn-ghost stp-btn-sm">{busy === 'cover' ? <span className="stp-spin" /> : <i className="fa-solid fa-image" />} {store.cover ? 'Change' : 'Upload'}
                <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={e => upload('cover', e.target.files?.[0])} /></label>
              {store.cover && <button type="button" className="stp-btn stp-btn-ghost stp-btn-sm" onClick={() => removeImg('cover')}>Remove</button>}
            </div>
          </div>
          <div className="stp-imgbox is-logo">
            {store.logo ? <img src={store.logo} alt="Logo" /> : <span>Logo</span>}
            <label className="stp-btn stp-btn-ghost stp-btn-sm">{busy === 'logo' ? <span className="stp-spin" /> : null}{store.logo ? 'Change' : 'Upload'}
              <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={e => upload('logo', e.target.files?.[0])} /></label>
          </div>
        </div>
        <label className="stp-field">Store name<input value={f.name} maxLength={80} onChange={e => set('name', e.target.value)} /></label>
        <label className="stp-field">One-line description<input value={f.about} maxLength={200} onChange={e => set('about', e.target.value)} placeholder="Fresh food delivered in Lekki & Ikeja" /></label>
      </section>

      <section className="stp-card" aria-labelledby="stp-wa">
        <h2 id="stp-wa">WhatsApp</h2>
        <label className="stp-field">WhatsApp number customers message<input inputMode="tel" value={f.waNumber} onChange={e => set('waNumber', e.target.value)} placeholder="2348031234567" />
          <small className="stp-hint">The number of this bot. Used for the store link’s “Send order on WhatsApp” button.</small></label>
        <label className="stp-field">Words that open the store<input value={f.keywords} onChange={e => set('keywords', e.target.value)} placeholder="shop, store, menu" />
          <small className="stp-hint">Separate with commas. If one of your bot commands uses the same word, the store answers it.</small></label>
        <label className="stp-field">Welcome message<textarea value={f.welcome} maxLength={600} onChange={e => set('welcome', e.target.value)} /></label>
        <div className="stp-row"><span><b>Taking orders</b><small>Turn off when you’re closed — customers get your closed message</small></span><Switch on={f.isOpen} onChange={v => set('isOpen', v)} label="Taking orders" /></div>
        {!f.isOpen && <label className="stp-field">Closed message<input value={f.closedMsg} maxLength={300} onChange={e => set('closedMsg', e.target.value)} placeholder="We’re closed now — we open at 9am tomorrow." /></label>}
      </section>
      <section className="stp-card" aria-labelledby="stp-alerts">
        <h2 id="stp-alerts">Alerts on your phone</h2>
        <p className="stp-muted">New orders arrive on WhatsApp with buttons to move them along (Start preparing → Out for delivery → Delivered). Type <b>/orders</b>, <b>/sales</b> or <b>/stock</b> to the bot any time.</p>
        <div className="stp-row"><span><b>Order alerts</b><small>Sent to the admin phones linked to this bot</small></span><Switch on={f.alerts} onChange={v => set('alerts', v)} label="Order alerts" /></div>
        <label className="stp-field">Warn me when stock drops to
          <select value={f.lowStock} onChange={e => set('lowStock', e.target.value)}>
            <option value="0">Never</option>
            {[1, 2, 3, 5, 10].map(n => <option key={n} value={n}>{n} left</option>)}
          </select></label>
        {admins.length ? (
          <ul className="stp-admins">
            {admins.map((a, i) => <li key={i}><i className="fa-brands fa-whatsapp" aria-hidden="true" /><span><b>{a.name}</b> · {a.phone}</span>
              <span className={`stp-pill is-${a.alerts ? 'ok' : 'muted'}`}>{a.alerts ? 'Alerts on' : 'Alerts off'}</span></li>)}
          </ul>
        ) : (
          <p className="stp-note">No admin phone is linked to this bot yet. Open the bot’s <b>WhatsApp admin</b> tab and link your phone to get order alerts.</p>
        )}
        <small className="stp-hint">WhatsApp only delivers to a phone that messaged the bot in the last 24 hours — send it anything once a day (e.g. /orders). Missed alerts are counted, and you also get every order by email.</small>
      </section>
      <button type="button" className="stp-btn stp-btn-dark stp-self-end" onClick={save} disabled={busy === 'save'}>{busy === 'save' ? <span className="stp-spin" /> : null}Save settings</button>
    </div>
  );
}
